import { Link, useLocation } from "@tanstack/react-router";
import type React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CollapsibleToolPanel } from "@/components/common/CollapsibleToolPanel";
import { NavItem } from "@/components/ui/nav-item";
import { ChatList } from "@/features/chat/components/ChatList";
import { StudyGroupList } from "@/features/my-study-groups/components/StudyGroupList";

// ナビゲーションアイテムの定義
const NAV_ITEMS = [
  {
    to: "/favorites",
    iconSrc: "/favorite.svg",
    labelKey: "common.favorites",
    pathMatch: "favorites",
  },
  {
    to: "/event-search",
    iconSrc: "/search.svg",
    labelKey: "common.eventSearch",
    pathMatch: "event-search",
  },
  {
    to: "/all-study-groups",
    iconSrc: "/add.svg",
    labelKey: "common.allStudyGroups",
    pathMatch: "all-study-groups",
  },
] as const;

type ToolKey = "study-groups" | "chat";
const COLLAPSED_TOOL_HEIGHT = "40px";
const ACTIVE_TOOL_STORAGE_KEY = "sidebar.activeTool";

export const Sidebar: React.FC = () => {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const [activeTool, setActiveTool] = useState<ToolKey>(() => {
    const stored = localStorage.getItem(ACTIVE_TOOL_STORAGE_KEY);
    return stored === "chat" || stored === "study-groups"
      ? stored
      : "study-groups";
  });

  useEffect(() => {
    localStorage.setItem(ACTIVE_TOOL_STORAGE_KEY, activeTool);
  }, [activeTool]);
  const handleToolSelect = (tool: ToolKey) => {
    setActiveTool((prev) => {
      if (prev !== tool) {
        return tool;
      }
      return tool === "chat" ? "study-groups" : "chat";
    });
  };

  return (
    <aside
      className="relative flex h-full min-h-0 w-[307px] flex-col border-r-2 border-study-group-selected bg-variable-collection-light-gray py-[26px]"
      style={
        {
          "--sidebar-width": "307px",
          "--sidebar-padding-left": "20px",
          "--sidebar-padding-right": "30px",
        } as React.CSSProperties
      }
    >
      {/* ロゴ・タイトル部分 */}
      <Link
        to="/"
        className="mb-[22px] block transition-opacity hover:opacity-80"
      >
        <h1 className="pl-4 text-xl font-[number:var(--logo-font-weight)] text-variable-collection-black-gray">
          Study Group Lounge
        </h1>
      </Link>

      {/* ナビゲーション部分 */}
      <nav className="mb-[22px] w-full min-w-0 space-y-[22px] overflow-x-hidden pr-[50px]">
        {NAV_ITEMS.map((item) => {
          const isActive = pathname.includes(item.pathMatch);
          return (
            <NavItem
              key={item.to}
              to={item.to}
              icon={<img src={item.iconSrc} alt="" className="size-[18px]" />}
              label={t(item.labelKey)}
              isActive={isActive}
            />
          );
        })}
      </nav>

      {/* スタディグループ/チャットリスト */}
      <div className="flex min-h-0 flex-1 flex-col gap-3 pl-[20px] pr-[30px]">
        <CollapsibleToolPanel
          isExpanded={activeTool === "study-groups"}
          collapsedHeight={COLLAPSED_TOOL_HEIGHT}
        >
          <StudyGroupList
            containerHeight="100%"
            containerClassName="h-full p-0"
            isExpanded={activeTool === "study-groups"}
            onHeaderToggle={() => handleToolSelect("study-groups")}
          />
        </CollapsibleToolPanel>
        <CollapsibleToolPanel
          isExpanded={activeTool === "chat"}
          collapsedHeight={COLLAPSED_TOOL_HEIGHT}
        >
          <ChatList
            containerHeight="100%"
            containerClassName="h-full p-0"
            showActions={activeTool === "chat"}
            isExpanded={activeTool === "chat"}
            onHeaderToggle={() => handleToolSelect("chat")}
            showTabs={activeTool === "chat"}
          />
        </CollapsibleToolPanel>
      </div>
    </aside>
  );
};
